import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Typography, Switch, message, Row, List, Card, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import * as promotionsApi from '../../services/api/promotionsApi';
import type { Promotion } from '../../services/api/promotionsApi';
import { money } from './banners';
import { BANNER_LABELS } from './PromotionBuilder';
import { useBreakpointDown } from '../../constants/breakpoints';

const { Title } = Typography;

/**
 * The promotion list — reached at `/owner/promotions`. "Add Promotion" opens
 * the builder (PromotionBuilder) at `/owner/promotions/new`; a row opens it
 * at `/owner/promotions/:id` to edit.
 */
const PromotionList: React.FC = () => {
  const navigate = useNavigate();
  // Below md the six-column table clipped its rightmost column (the active
  // toggle) off the edge of a 390px phone with no scroll affordance, so the
  // owner could neither see nor reach it. Cards below, table above — the same
  // split EstimateForm/EstimateDetail already use.
  const isNarrow = useBreakpointDown('md');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await promotionsApi.listPromotions();
      setPromotions(rows);
    } catch {
      message.error('Failed to load promotions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleActive = async (promotion: Promotion, isActive: boolean) => {
    setTogglingId(promotion.id);
    try {
      const updated = await promotionsApi.updatePromotion(promotion.id, { is_active: isActive ? 1 : 0 });
      setPromotions((prev) => prev.map((p) => (p.id === promotion.id ? updated : p)));
    } catch {
      message.error('Failed to update promotion.');
    } finally {
      setTogglingId(null);
    }
  };

  const priceLabel = (promotion: Promotion) => {
    if (promotion.promo_price_cents == null) return '—';
    if (promotion.list_price_cents != null && promotion.list_price_cents > promotion.promo_price_cents) {
      return `${money(promotion.promo_price_cents)} (was ${money(promotion.list_price_cents)})`;
    }
    return money(promotion.promo_price_cents);
  };

  const windowLabel = (promotion: Promotion) => {
    if (!promotion.starts_at && !promotion.ends_at) return 'Always on';
    const start = promotion.starts_at ? new Date(promotion.starts_at).toLocaleDateString() : '…';
    const end = promotion.ends_at ? new Date(promotion.ends_at).toLocaleDateString() : '…';
    return `${start} – ${end}`;
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Promotions</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/owner/promotions/new')}>
          New Promotion
        </Button>
      </Row>

      {isNarrow ? (
        <List
          loading={loading}
          dataSource={promotions}
          rowKey={(record: Promotion) => String(record.id)}
          pagination={{ pageSize: 20 }}
          renderItem={(record: Promotion) => (
            <List.Item style={{ padding: 0, marginBottom: 12 }}>
              <Card
                size="small"
                style={{ width: '100%' }}
                onClick={() => navigate(`/owner/promotions/${record.id}`)}
                title={record.name}
                extra={
                  <span onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={!!record.is_active}
                      loading={togglingId === record.id}
                      onChange={(checked) => handleToggleActive(record, checked)}
                    />
                  </span>
                }
              >
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <span>{priceLabel(record)}</span>
                  <span style={{ color: '#6b7580', fontSize: 12 }}>
                    {BANNER_LABELS[record.banner_design] ?? record.banner_design} · {windowLabel(record)}
                  </span>
                  <span>
                    {!!record.show_on_receipts && <Tag>Receipts</Tag>}
                    {!!record.show_on_portal && <Tag>Portal</Tag>}
                  </span>
                </Space>
              </Card>
            </List.Item>
          )}
        />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={promotions}
          scroll={{ x: true }}
          pagination={{ pageSize: 20 }}
          onRow={(record) => ({ onClick: () => navigate(`/owner/promotions/${record.id}`) })}
          columns={[
            { title: 'Name', dataIndex: 'name', key: 'name' },
            {
              title: 'Banner',
              dataIndex: 'banner_design',
              key: 'banner_design',
              render: (design: Promotion['banner_design']) => BANNER_LABELS[design] ?? design,
            },
            { title: 'Price', key: 'price', render: (_: unknown, record: Promotion) => priceLabel(record) },
            { title: 'Window', key: 'window', render: (_: unknown, record: Promotion) => windowLabel(record) },
            {
              title: 'Active',
              key: 'is_active',
              render: (_: unknown, record: Promotion) => (
                <Switch
                  checked={!!record.is_active}
                  loading={togglingId === record.id}
                  onClick={(checked, e) => e.stopPropagation()}
                  onChange={(checked) => handleToggleActive(record, checked)}
                />
              ),
            },
            {
              title: 'Shown',
              key: 'shown',
              render: (_: unknown, record: Promotion) => (
                <>
                  {!!record.show_on_receipts && <Tag>Receipts</Tag>}
                  {!!record.show_on_portal && <Tag>Portal</Tag>}
                </>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default PromotionList;
